import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Switch,
    FormControlLabel,
    Button,
    Divider,
    Alert,
    Grid,
    InputAdornment,
    Tabs,
    Tab,
} from '@mui/material';
import {
    Save,
    AccessTime,
    EventRepeat,
    CalendarMonth,
    Restore,
    Schedule,
    Add,
} from '@mui/icons-material';
import VisualAvailabilityCalendar from './VisualAvailabilityCalendar';
import MonthlyCalendarScheduler from './MonthlyCalendarScheduler';

interface Reservation {
    id: string;
    clientId?: string;
    customerName: string;
    customerEmail: string;
    startDate: string;
    endDate: string;
    status: string;
    statusHistory?: any[];
    paymentStatus?: 'pending' | 'partial' | 'paid' | 'refunded';
    completionStatus?: 'scheduled' | 'confirmed' | 'in_progress' | 'completed' | 'no_show' | 'cancelled';
    rescheduleCount?: number;
    lastRescheduleDate?: string;
    notes?: string;
}

interface Client {
    id: string;
    name: string;
    email: string;
}

interface RecurringTemplate {
    id?: string;
    clientId: string;
    clientName: string;
    pattern: 'daily' | 'weekly' | 'monthly';
    daysOfWeek?: number[];
    dayOfMonth?: number;
    startTime: string;
    endTime: string;
    duration: number;
    startDate: Date;
    endDate?: Date;
    isActive: boolean;
}

interface ManualShift {
    id?: string;
    date: Date;
    startTime: string;
    endTime: string;
    duration: number;
    type: 'available' | 'blocked' | 'maintenance' | 'event';
    title: string;
    description?: string;
    isActive: boolean;
}

interface AgendaSettingsProps {
    settings: any;
    reservations?: Reservation[];
    clients?: Client[];
    recurringTemplates?: RecurringTemplate[];
    manualShifts?: ManualShift[];
    onSave: (newSettings: any) => Promise<void>;
    onCreateClient?: (clientData: { name: string; email: string; phone?: string }) => Promise<Client>;
    onSaveTemplate?: (template: RecurringTemplate) => Promise<void>;
    onDeleteTemplate?: (templateId: string) => Promise<void>;
    onSaveShift?: (shift: ManualShift) => Promise<void>;
    onDeleteShift?: (shiftId: string) => Promise<void>;
    onReservationClick?: (reservation: Reservation) => void;
}

const defaultSettings = {
    turnsPerHour: 1,
    timeSlotDuration: 60, // minutes
    allow247: false,
    weeklyAvailability: {
        lunes: [],
        martes: [],
        miercoles: [],
        jueves: [],
        viernes: [],
        sabado: [],
        domingo: []
    },
    blockedDates: {} as Record<string, string>
};

const AgendaSettings: React.FC<AgendaSettingsProps> = ({
    settings,
    reservations = [],
    clients = [],
    recurringTemplates = [],
    manualShifts = [],
    onSave,
    onCreateClient,
    onSaveTemplate,
    onDeleteTemplate,
    onSaveShift,
    onDeleteShift,
    onReservationClick
}) => {
    const [localSettings, setLocalSettings] = useState(defaultSettings);
    const [blockedDates, setBlockedDates] = useState<Record<string, string>>({});
    const [hasChanges, setHasChanges] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        if (settings) {
            setLocalSettings({
                ...defaultSettings,
                ...settings,
                weeklyAvailability: {
                    ...defaultSettings.weeklyAvailability,
                    ...(settings.weeklyAvailability || {})
                }
            });
            setBlockedDates(settings.blockedDates || {});
        }
    }, [settings]);

    const handleChange = (field: string, value: any) => {
        setLocalSettings(prev => ({
            ...prev,
            [field]: value
        }));
        setHasChanges(true);
        setMessage(null);
    };

    const handleAvailabilityChange = (newAvailability: any) => {
        handleChange('weeklyAvailability', newAvailability);
    };

    const handleUpdateBlockedDates = (newBlockedDates: Record<string, string>) => {
        setBlockedDates(newBlockedDates);
        setHasChanges(true);
        setMessage(null);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave({
                ...localSettings,
                blockedDates: blockedDates
            });
            setHasChanges(false);
            setMessage({ type: 'success', text: 'Configuración guardada correctamente' });
        } catch (error) {
            setMessage({ type: 'error', text: 'Error al guardar la configuración' });
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        if (settings) {
            setLocalSettings({
                ...defaultSettings,
                ...settings,
                weeklyAvailability: {
                    ...defaultSettings.weeklyAvailability,
                    ...(settings.weeklyAvailability || {})
                }
            });
            setHasChanges(false);
            setMessage(null);
        }
    };

    const tabContent = [
        {
            label: 'Disponibilidad Semanal',
            icon: <CalendarMonth />,
            content: (
                <Grid container spacing={4}>
                    {/* General Settings */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTime fontSize="small" /> Parámetros Generales
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                            <TextField
                                label="Duración del Turno"
                                type="number"
                                value={localSettings.timeSlotDuration}
                                onChange={(e) => handleChange('timeSlotDuration', parseInt(e.target.value) || 15)}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">min</InputAdornment>,
                                }}
                                helperText="Tiempo que dura cada reserva"
                            />

                            <TextField
                                label="Turnos Simultáneos"
                                type="number"
                                value={localSettings.turnsPerHour}
                                onChange={(e) => handleChange('turnsPerHour', parseInt(e.target.value) || 1)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><EventRepeat /></InputAdornment>,
                                }}
                                helperText="Cuántas reservas aceptas al mismo tiempo"
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={localSettings.allow247}
                                        onChange={(e) => handleChange('allow247', e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Abierto 24/7 (Sin restricciones horarias)"
                            />
                        </Box>
                    </Grid>

                    {/* Visual Calendar */}
                    <Grid size={{ xs: 12, md: 8 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarMonth fontSize="small" /> Disponibilidad Semanal
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Haz clic en las horas para activar/desactivar
                            </Typography>
                        </Box>

                        {!localSettings.allow247 ? (
                            <VisualAvailabilityCalendar
                                availability={localSettings.weeklyAvailability}
                                onChange={handleAvailabilityChange}
                            />
                        ) : (
                            <Alert severity="info">
                                El modo 24/7 está activado. La agenda estará disponible en todos los horarios.
                            </Alert>
                        )}
                    </Grid>
                </Grid>
            ),
        },
        {
            label: 'Calendario Mensual',
            icon: <CalendarMonth />,
            content: (
                <MonthlyCalendarScheduler
                    events={reservations.map(r => ({
                        id: r.id,
                        date: new Date(r.startDate).toISOString().split('T')[0],
                        type: 'reservation' as const,
                        title: `${r.customerName} - ${new Date(r.startDate).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`,
                        description: r.notes || '',
                        clientName: r.customerName,
                        status: r.completionStatus,
                        paymentStatus: r.paymentStatus,
                    }))}
                    weeklyAvailability={localSettings.weeklyAvailability}
                    allow247={localSettings.allow247}
                    blockedDates={blockedDates}
                    onUpdateBlockedDates={handleUpdateBlockedDates}
                    onReservationClick={onReservationClick}
                />
            ),
        },
    ];

    return (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <CalendarMonth color="primary" sx={{ fontSize: 32 }} />
                <Box>
                    <Typography variant="h5" fontWeight="bold">
                        Gestión Avanzada de Agenda
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Configura disponibilidad semanal y mensual, y bloquea días específicos
                    </Typography>
                </Box>
            </Box>

            {message && (
                <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
                    {message.text}
                </Alert>
            )}

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
                mb: 3
            }}>
                {tabContent.map((tab, index) => (
                    <Button
                        key={index}
                        variant={activeTab === index ? "contained" : "outlined"}
                        startIcon={tab.icon}
                        onClick={() => setActiveTab(index)}
                        sx={{
                            justifyContent: 'flex-start',
                            py: 1.5,
                            borderRadius: 2,
                            borderWidth: activeTab === index ? 0 : 1,
                            bgcolor: activeTab === index ? 'primary.main' : 'background.paper',
                            color: activeTab === index ? 'white' : 'text.primary',
                            borderColor: 'divider',
                            boxShadow: activeTab === index ? 4 : 0,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: 2,
                                bgcolor: activeTab === index ? 'primary.dark' : 'background.paper',
                                borderColor: 'text.primary'
                            }
                        }}
                    >
                        {tab.label}
                    </Button>
                ))}
            </Box>

            <Box sx={{ mb: 4 }}>
                {tabContent[activeTab].content}
            </Box>

            {(activeTab === 0 || activeTab === 1) && (
                <>
                    <Divider sx={{ my: 4 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<Restore />}
                            onClick={handleReset}
                            disabled={!hasChanges || saving}
                        >
                            Deshacer Cambios
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<Save />}
                            onClick={handleSave}
                            disabled={!hasChanges || saving}
                            sx={{
                                bgcolor: 'primary.main',
                                '&:hover': { bgcolor: 'primary.dark' },
                                px: 4
                            }}
                        >
                            {saving ? 'Guardando...' : 'Guardar Configuración'}
                        </Button>
                    </Box>
                </>
            )}
        </Paper>
    );
};

export default AgendaSettings;
